import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import {
  getPetAssetKey,
  speciesAssetsRatio,
  speciesChatbot,
  SpeciesChatbotType,
} from './species';

/**
 * Assets for visited messages
 */
export const visitedAssets = {
  'background/visited': 'private/chatbot/visit/friendvisit_background.png',
};

const petSizes: Record<SpeciesChatbotType, 'full' | `${number}%`> = {
  dog: 'full',
  ball: '80%',
  starball: '80%',
  crab: 'full',
  hamster: 'full',
  turtle: 'full',
  seaotter: '90%',
  cat: 'full',
  whale: 'full',
  giraffe: 'full',
};

/**
 * Create visited message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const visitedMessage = createFlexMessageTemplate(
  SB.object({
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja']),
    petName: SB.string(),
    visitor: SB.object({
      name: SB.string(),
      photo: SB.string(),
      pet: SB.tuple(speciesChatbot),
    }),
  }),
  (data, payload, api) => {
    const text = getLocaleConfig(data.locale).visit.finish.text.replace(
      '{name}',
      data.visitor.name,
    );
    const petAssetKey = getPetAssetKey(data.species, 'happy');
    const petIcon = api.getAssetPath(petAssetKey);
    const friendAssetKey = getPetAssetKey(data.visitor.pet, 'happy');
    const friendIcon = api.getAssetPath(friendAssetKey);

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('background/visited'),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.3:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'horizontal',
            width: '100%',
            height: '26.5%',
            position: 'absolute',
            offsetTop: '5.75%',
            paddingStart: '5%',
            paddingEnd: '5%',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                width: '22.5%',
                position: 'relative',
                flex: 0,
                contents: [
                  {
                    type: 'image',
                    url: data.visitor.photo,
                    aspectRatio: '1:1',
                    aspectMode: 'cover',
                    size: 'full',
                  },
                ],
              },
              {
                type: 'text',
                text,
                weight: 'bold',
                color: '#ffffff',
                size: 'xl',
                margin: 'xl',
                flex: 1,
                wrap: true,
                gravity: 'center',
              },
            ],
          },
          {
            type: 'box',
            layout: 'horizontal',
            position: 'absolute',
            width: '100%',
            paddingStart: '12.5%',
            paddingEnd: '12.5%',
            alignItems: 'flex-end',
            height: '76%',
            contents: [
              {
                type: 'image',
                url: friendIcon,
                aspectRatio: speciesAssetsRatio[friendAssetKey],
                size: petSizes[data.visitor.pet],
              },
              {
                type: 'image',
                url: petIcon,
                aspectRatio: speciesAssetsRatio[petAssetKey],
                size: petSizes[data.species],
              },
            ],
          },
        ],
      },
    });

    // Add sender to message
    template['sender'] = {
      name: data.petName,
      iconUrl: api.getAssetPath(getPetAssetKey(data.species)),
    };

    return template;
  },
);
